export type CampaignObjectId = string;
export type CampaignIsoDateTime = string;

export const CAMPAIGN_TYPES = {
  INFORMATIVO: 'informativo',
  ALERTA: 'alerta',
  NOVIDADES: 'novidades'
} as const;

export type CampaignType = typeof CAMPAIGN_TYPES[keyof typeof CAMPAIGN_TYPES];

export enum CampaignMediaType {
  IMAGE = 'image',
  VIDEO = 'video'
}

export enum CampaignStatus {
  DRAFT = 'draft',
  PUBLISHED = 'published',
  SCHEDULED = 'scheduled',
  ACTIVE = 'active',
  PAUSED = 'paused',
  EXPIRED = 'expired',
  ARCHIVED = 'archived'
}

export enum CampaignDestinationType {
  WHATSAPP = 'whatsapp'
}

export enum CampaignEventType {
  ELIGIBILITY_EVALUATED = 'eligibility-evaluated',
  POPUP_DISPLAYED = 'popup-displayed',
  POPUP_VIEWED = 'popup-viewed',
  POPUP_DISMISSED = 'popup-dismissed',
  WHATSAPP_CLICKED = 'whatsapp-clicked',
  VIDEO_STARTED = 'video-started',
  VIDEO_PROGRESS = 'video-progress',
  VIDEO_COMPLETED = 'video-completed'
}

export enum CampaignEventSource {
  ADMIN_PANEL = 'admin-panel',
  API = 'api',
  AUTOMATIC_POPUP = 'automatic-popup',
  SYSTEM = 'system',
  WEB_APP = 'web-app'
}

export enum CampaignDeliveryStatus {
  PENDING = 'pending',
  DELIVERED = 'delivered',
  POPUP_DISPLAYED = 'popup-displayed',
  POPUP_VIEWED = 'popup-viewed',
  POPUP_DISMISSED = 'popup-dismissed',
  WHATSAPP_CLICKED = 'whatsapp-clicked',
  VIDEO_STARTED = 'video-started',
  VIDEO_PROGRESS = 'video-progress',
  VIDEO_COMPLETED = 'video-completed',
  FAILED = 'failed',
  EXPIRED = 'expired'
}

export enum CampaignUserStateStatus {
  ELIGIBLE = 'eligible',
  POPUP_DISPLAYED = 'popup-displayed',
  POPUP_VIEWED = 'popup-viewed',
  POPUP_DISMISSED = 'popup-dismissed',
  WHATSAPP_CLICKED = 'whatsapp-clicked',
  VIDEO_STARTED = 'video-started',
  VIDEO_PROGRESS = 'video-progress',
  VIDEO_COMPLETED = 'video-completed',
  INELIGIBLE = 'ineligible'
}

export interface CampaignSchedule {
  startsAt: CampaignIsoDateTime;
  endsAt?: CampaignIsoDateTime;
  timeZone?: string;
}

export interface CampaignMediaAsset {
  mediaType: CampaignMediaType;
  mediaUrl: string;

  altText?: string;
  title?: string;
  durationInSeconds?: number;
}

export interface CampaignWhatsAppCallToAction {
  label: string;
  destinationType: CampaignDestinationType.WHATSAPP;
  whatsappPhoneNumber?: string;
  whatsappMessageTemplate?: string;
}

export interface CampaignAudience {
  cnpjs?: string[];
  currentState?: number;
}

export interface CampaignDisplaySettings {
  popupEnabled: boolean;
  showOncePerUser: boolean;
  canBeDismissed: boolean;
  displayPriority: number;
}

export interface CampaignMetricsSnapshot {
  eligibleUsersCount?: number;
  popupDisplayCount?: number;
  popupViewCount?: number;
  popupDismissCount?: number;
  whatsappClickCount?: number;
  videoStartCount?: number;
  videoProgressCount?: number;
  videoCompleteCount?: number;
  conversionRate?: number;
  lastAggregatedAt?: CampaignIsoDateTime;
}

export interface CampaignDeliveryMatchCriteria {
  cnpjs: boolean;
  currentState: boolean;
}

export interface CampaignEventMetadata {
  reason?: string;
  userAgent?: string;
  ipAddress?: string;
  referrer?: string;
  additionalData?: Record<string, unknown>;
}

export interface Campaign {
  id?: CampaignObjectId;
  _id?: CampaignObjectId;
  name: string;
  headline: string;
  bodyText?: string;
  campaignType: CampaignType;
  status: CampaignStatus;
  schedule: CampaignSchedule;
  mediaAsset: CampaignMediaAsset;
  whatsappCallToAction?: CampaignWhatsAppCallToAction;
  audience: CampaignAudience;
  displaySettings: CampaignDisplaySettings;
  metricsSnapshot?: CampaignMetricsSnapshot;
  publishedAt?: CampaignIsoDateTime;
  createdAt?: CampaignIsoDateTime;
  updatedAt?: CampaignIsoDateTime;
}

export interface CampaignDelivery {
  id?: CampaignObjectId;
  _id?: CampaignObjectId;
  campaignId: CampaignObjectId;
  companyId: CampaignObjectId;
  deliveryStatus: CampaignDeliveryStatus;
  deliverySource: CampaignEventSource;
  matchedBy?: CampaignDeliveryMatchCriteria;
  distributedAt?: CampaignIsoDateTime;
  popupDisplayedAt?: CampaignIsoDateTime;
  popupViewedAt?: CampaignIsoDateTime;
  popupDismissedAt?: CampaignIsoDateTime;
  whatsappClickedAt?: CampaignIsoDateTime;
  videoStartedAt?: CampaignIsoDateTime;
  videoProgressAt?: CampaignIsoDateTime;
  videoCompletedAt?: CampaignIsoDateTime;
  expiresAt?: CampaignIsoDateTime;
  attemptCount?: number;
  failureReason?: string;
  createdAt?: CampaignIsoDateTime;
  updatedAt?: CampaignIsoDateTime;
}

export interface CampaignUserState {
  id?: CampaignObjectId;
  _id?: CampaignObjectId;
  campaignId: CampaignObjectId;
  companyId: CampaignObjectId;
  userId: CampaignObjectId;
  status: CampaignUserStateStatus;
  firstEligibleAt?: CampaignIsoDateTime;
  popupDisplayedAt?: CampaignIsoDateTime;
  popupViewedAt?: CampaignIsoDateTime;
  popupDismissedAt?: CampaignIsoDateTime;
  whatsappClickedAt?: CampaignIsoDateTime;
  videoStartedAt?: CampaignIsoDateTime;
  videoProgressAt?: CampaignIsoDateTime;
  videoCompletedAt?: CampaignIsoDateTime;
  lastDeliveryId?: CampaignObjectId;
  hasSeenPopup: boolean;
  popupDisplayCount: number;
  popupViewCount: number;
  popupDismissCount: number;
  whatsappClickCount: number;
  videoStartCount: number;
  videoProgressCount: number;
  videoCompleteCount: number;
  lastEvaluatedAt?: CampaignIsoDateTime;
  lastInteractionAt?: CampaignIsoDateTime;
  createdAt?: CampaignIsoDateTime;
  updatedAt?: CampaignIsoDateTime;
}

export interface CampaignEvent {
  id?: CampaignObjectId;
  _id?: CampaignObjectId;
  campaignId: CampaignObjectId;
  companyId?: CampaignObjectId;
  userId?: CampaignObjectId;
  deliveryId?: CampaignObjectId;
  campaignUserStateId?: CampaignObjectId;
  eventType: CampaignEventType;
  eventSource: CampaignEventSource;
  occurredAt: CampaignIsoDateTime;
  metadata?: CampaignEventMetadata;
  createdAt?: CampaignIsoDateTime;
  updatedAt?: CampaignIsoDateTime;
}

export interface HighlightedCampaignEligibility {
  campaign: Campaign | null;
  delivery: CampaignDelivery | null;
  userState: CampaignUserState | null;
  evaluatedAt: CampaignIsoDateTime;
}

export interface EligibleCampaignListItem {
  campaign: Campaign;
  delivery: CampaignDelivery;
  userState: CampaignUserState | null;
}

export interface EligibleCampaignList {
  campaigns: EligibleCampaignListItem[];
  evaluatedAt: CampaignIsoDateTime;
}

export interface GetEligibleHighlightedCampaignParams {
  currentState?: number;
  cnpjs?: string[];
}

export interface CreateCampaignEventPayload {
  eventType: CampaignEventType;
  eventSource?: CampaignEventSource;
  deliveryId?: CampaignObjectId;
  campaignUserStateId?: CampaignObjectId;
  occurredAt?: CampaignIsoDateTime;
  metadata?: CampaignEventMetadata;
}
